import type * as React from 'react';
import type { CSSProperties } from 'react';
import type {
  AvailabilityGridData,
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellState,
  CellSelection,
  EntriesGridData,
  MultiCellSelection,
} from '../lib/availability-types';
import type { CellPopoverLabels } from './cell-popover-state';
import { CellTd, isCellClickable } from './availability-cell-td';

interface AvailabilityCellsRowProps {
  row: AvailabilityGridRow;
  periods: AvailabilityPeriod[];
  grid: AvailabilityGridData;
  /** Optional multi-stack entries per cell. When present, overrides the default single-icon rendering. */
  entriesGrid?: EntriesGridData;
  /** Column pixel width — passed to MultiStackCellContent for day-granularity degradation. */
  colWidth?: number;
  selection?: CellSelection | null;
  popoverLabels: CellPopoverLabels;
  soldAriaLabel: string;
  /** Accessible label for the OPTION-bucket status icon fallback (no customContent/entries). */
  optionAriaLabel?: string;
  /** Accessible label for the merged reserved-bucket status icon fallback (no customContent/entries). */
  reservedAriaLabel?: string;
  /**
   * When true, the grid never renders its internal CellPopover even when a cell is selected.
   * Cell highlighting via selection / isSelected still works.
   * Use on inventory and detail surfaces where the route renders its own external CellPopover.
   * Defaults to false — offer-draft tab relies on the grid-internal popover.
   */
  disableInternalPopover?: boolean;
  /**
   * When true, SOLD cells are treated as clickable so they can open a read-only
   * details popover. Defaults to false (offer-availability view: SOLD is final, no actions).
   */
  soldCellClickable?: boolean;
  getReservationStatusForPeriod?: (
    unitId: string,
    periodId: string,
  ) => 'SOFT_RESERVATION' | 'HARD_RESERVATION' | null;
  getOwnCellTtlExpiresAt?: (unitId: string, periodId: string) => Date | null | undefined;
  onTtlExpired?: () => void;
  selectedCellClassName?: string;
  selectedCellStyle?: CSSProperties;
  onCellClick?: (unitId: string, period: AvailabilityPeriod, shiftKey: boolean) => void;
  onClosePopover?: () => void;
  onReserve?: (selection: CellSelection, status: 'SOFT_RESERVATION' | 'OPTION') => void;
  onUpgradeHard?: (selection: CellSelection) => void;
  onCloseSale?: (selection: CellSelection) => void;
  onRelease?: (selection: CellSelection) => void;
  /**
   * Returns queue info for a given cell, or null when no queue data is available.
   * myPosition === null means the current user is not in the queue.
   */
  getCellQueueInfo?: (
    unitId: string,
    periodId: string,
  ) => { count: number; myPosition: number | null } | null;
  /** Called when the current user joins the waitlist queue for a cell. */
  onJoinQueue?: (selection: CellSelection) => void;
  /** Called when the current user leaves the waitlist queue for a cell. */
  onLeaveQueue?: (selection: CellSelection) => void;
  /** Drag-range selection: called on mousedown on a FREE cell to start a selection. */
  onCellMouseDown?: (unitId: string, period: AvailabilityPeriod) => void;
  /** Drag-range selection: called on mouseenter while drag is in progress. */
  onCellMouseEnter?: (unitId: string, period: AvailabilityPeriod) => void;
  /** Drag-range selection: called on mouseup to commit the selection. */
  onCellMouseUp?: (unitId: string, period: AvailabilityPeriod) => void;
  renderCellContent?: (
    row: AvailabilityGridRow,
    period: AvailabilityPeriod,
    cellState: CellState,
    colWidth: number,
  ) => React.ReactNode;
  /**
   * Controlled multi-cell selection (task 4.1's `multiSelect` state) — read-only here,
   * used only to drive the same visual highlight ring as the single-cell `selection`
   * prop for cells included in the active multi-select rectangle. See `AvailabilityGrid`'s
   * own `multiSelection` prop for the full controlled-state contract.
   */
  multiSelection?: MultiCellSelection | null;
}

/**
 * True when a multi-cell selection (task 4.1's `multiSelect` state) includes this cell —
 * drives the shared visual highlight ring alongside the single-cell `selection` prop.
 */
function isCellInMultiSelection(
  multiSelection: MultiCellSelection | null | undefined,
  unitId: string,
  periodId: string,
): boolean {
  return multiSelection?.cells.some((c) => c.unitId === unitId && c.periodId === periodId) ?? false;
}

function getCellViewModel(
  row: AvailabilityGridRow,
  period: AvailabilityPeriod,
  grid: AvailabilityGridData,
  selection: CellSelection | null,
  onClosePopover?: () => void,
  multiSelection?: MultiCellSelection | null,
) {
  const cellState: CellState = grid[row.id]?.[period.id] ?? 'free';
  const isSingleSelected = selection?.unitId === row.id && selection.periodIds.includes(period.id);
  const showPopover =
    isSingleSelected === true &&
    selection !== null &&
    onClosePopover !== undefined &&
    selection.periodIds[0] === period.id &&
    selection.unitId === row.id;

  return {
    cellState,
    isSelected:
      isSingleSelected === true || isCellInMultiSelection(multiSelection, row.id, period.id),
    showPopover,
  };
}

export function AvailabilityCellsRow({
  row,
  periods,
  grid,
  entriesGrid,
  colWidth,
  selection = null,
  popoverLabels,
  soldAriaLabel,
  optionAriaLabel,
  reservedAriaLabel,
  disableInternalPopover = false,
  soldCellClickable = false,
  getReservationStatusForPeriod,
  getOwnCellTtlExpiresAt,
  onTtlExpired,
  selectedCellClassName,
  selectedCellStyle,
  onCellClick,
  onClosePopover,
  onReserve,
  onUpgradeHard,
  onCloseSale,
  onRelease,
  getCellQueueInfo,
  onJoinQueue,
  onLeaveQueue,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  renderCellContent,
  multiSelection,
}: AvailabilityCellsRowProps) {
  return (
    <>
      {periods.map((period) => {
        const { cellState, isSelected, showPopover } = getCellViewModel(
          row,
          period,
          grid,
          selection,
          onClosePopover,
          multiSelection,
        );
        const isClickable = isCellClickable(cellState, onCellClick, soldCellClickable);
        const customContent = renderCellContent?.(row, period, cellState, colWidth ?? 80);
        const entries = entriesGrid?.[row.id]?.[period.id];

        return (
          <CellTd
            key={period.id}
            row={row}
            period={period}
            cellState={cellState}
            isSelected={isSelected}
            isClickable={isClickable}
            showPopover={showPopover}
            disableInternalPopover={disableInternalPopover}
            selection={selection}
            periods={periods}
            grid={grid}
            entries={entries}
            colWidth={colWidth}
            popoverLabels={popoverLabels}
            soldAriaLabel={soldAriaLabel}
            optionAriaLabel={optionAriaLabel}
            reservedAriaLabel={reservedAriaLabel}
            getReservationStatusForPeriod={getReservationStatusForPeriod}
            getOwnCellTtlExpiresAt={getOwnCellTtlExpiresAt}
            onTtlExpired={onTtlExpired}
            selectedCellClassName={selectedCellClassName}
            selectedCellStyle={selectedCellStyle}
            onCellClick={onCellClick}
            onClosePopover={onClosePopover}
            onReserve={onReserve}
            onUpgradeHard={onUpgradeHard}
            onCloseSale={onCloseSale}
            onRelease={onRelease}
            queueInfo={getCellQueueInfo?.(row.id, period.id) ?? undefined}
            onJoinQueue={onJoinQueue}
            onLeaveQueue={onLeaveQueue}
            onCellMouseDown={onCellMouseDown}
            onCellMouseEnter={onCellMouseEnter}
            onCellMouseUp={onCellMouseUp}
            customContent={customContent}
          />
        );
      })}
    </>
  );
}
